// ============================================================
// FILE: _services/fetal-doppler/fetal-doppler-protocol.ts
//
// Ported from EzovionOPDFrontend (Angular 20) core/services/keyar-echo/
// keyar-echo-protocol.ts. That file's own header traces this protocol back
// further, to the reference AaboWebApp20 project's fetal-doppler-protocol.ts
// -- every byte-level protocol fact below (UUIDs, frame tables, ADPCM
// tables, write-command encodings) is unchanged from that source. Only
// names were adjusted for this app's naming convention (per the confirmed
// migration decision to keep this app's existing "Fetal Doppler" label
// rather than the vendor's "Keyar Echo" branding): KeyarEcho* -> FetalDoppler*.
// No Angular/RxJS dependency, so this is portable to any JS runtime -- do
// not "clean up" the offsets/tables without re-verifying against real
// hardware, per the provenance notes below.
//
// Pure protocol layer for the Keyar Echo (vendor: LuckCome / 莱康宁,
// product family "OctobarGoodBaby") fetal Doppler, sold in this app as
// "Fetal Doppler" -- no Angular/RxJS dependency.
//
// Confirmed via disassembly of the vendor's own compiled iOS SDK
// (LMTPDecoder.a) -- otool -tV on the extracted .o, immediate values
// recomputed precisely in Python rather than hand-read off the assembly.
// Each encoder's parameter range was cross-checked against LMTPDecoder.h's
// own documented range for that command and matches exactly (FHR volume
// 0-7, alarm value 0-7, alarm level 0-3, TOCO reset only fires when
// isToco==1 per the header's own comment) -- that agreement is the basis
// for calling these confirmed rather than guessed. This is legitimate
// interoperability inspection of a library the vendor delivered for
// integration, not reverse-engineering of an obfuscated bundle.
//
// The READ side (fetal/maternal heart-rate values) is implemented against a
// vendor-authored protocol document (Keyar_Echo_BLE_Integration_Reference.docx)
// giving the exact frame layout, byte offsets, and reassembly algorithm for
// all 9 frame types.
// ============================================================

export function encodeFhrVolumeCommand(volume: number): Uint8Array {
  if (!Number.isInteger(volume) || volume < 0 || volume > 7) {
    throw new Error(`FHR volume must be an integer 0-7, got ${volume}`);
  }
  return new Uint8Array([0x54, 0xaa, 0x0a, volume, 0xff, 0xff, 0xff, 0xff, (volume - 4) & 0xff]);
}

export function encodeFhrAlarmValueCommand(volume: number): Uint8Array {
  if (!Number.isInteger(volume) || volume < 0 || volume > 7) {
    throw new Error(`FHR alarm volume must be an integer 0-7, got ${volume}`);
  }
  return new Uint8Array([0x54, 0xaa, 0x0a, 0xff, 0xff, 0xff, volume, 0xff, (volume - 4) & 0xff]);
}

export function encodeFhrAlarmLevelCommand(level: number): Uint8Array {
  if (!Number.isInteger(level) || level < 0 || level > 3) {
    throw new Error(`FHR alarm level must be an integer 0-3, got ${level}`);
  }
  return new Uint8Array([0x55, 0xaa, 0x0a, 0x55, 0xff, 0xff, 0xff, level, (level - 4) & 0xff]);
}

// Matches the vendor's own behavior exactly: LMTPDecoder's compiled code
// only builds/sends this command when isToco==1 ("1 代表宫缩复位" -- "1
// means TOCO reset" per LMTPDecoder.h's own comment); isToco==0 is a silent
// no-op in the original, so this returns null rather than an
// all-placeholder packet for that case.
export function encodeTocoResetCommand(isToco: 0 | 1, tocoResetValue: number): Uint8Array | null {
  if (isToco !== 1) {
    return null;
  }
  if (!Number.isInteger(tocoResetValue) || tocoResetValue < 0 || tocoResetValue > 3) {
    throw new Error(`TOCO reset value must be an integer 0-3, got ${tocoResetValue}`);
  }
  return new Uint8Array([0x54, 0xaa, 0x0a, 0xff, tocoResetValue, 0x01, 0xff, 0xff, (tocoResetValue - 2) & 0xff]);
}

// Confirmed from the vendor's own compiled code: all four commands write
// with response (CBCharacteristicWriteWithResponse on iOS -- the `type`
// argument LMTPDecoder passes to -[CBPeripheral writeValue:forCharacteristic
// :type:] -- equivalently WRITE_TYPE_DEFAULT on Android).
export const WRITE_WITH_RESPONSE = true;

// Field names are taken directly from the vendor's own LKCDecodeHeart.h data
// model (rate/rate2/tocoValue/mhr/battValue/signal/afm/afmFlag/fmFlag/
// tocoFlag), translated to camelCase for this project's convention. Decoded
// by parseFetalDopplerFrame() below, per Keyar_Echo_BLE_Integration_
// Reference.docx. fmFlag/tocoFlag (manualMovementMarked/tocoResetMarked)
// are NOT part of that document's frame layout -- left undecoded rather
// than guessed at; they stay optional/unset from the read path.
export interface FetalDopplerReading {
  fetalHeartRate?: number;        // rate -- primary fetal heart rate, bpm. 0 = none.
  fetalHeartRate2?: number;       // rate2 -- second fetal heart rate (twins), bpm. 0 = none.
  tocoValue?: number;             // uterine contraction pressure, 0-100.
  maternalHeartRate?: number;     // mhr
  batteryLevel?: number;          // battValue, 0-4 (device-native scale, not a percent)
  signalQuality?: number;         // signal, 0-3
  fetalMovementCurve?: number;    // afm, 0-40 (automatic fetal-movement curve)
  autoMovementDetected?: boolean; // afmFlag
  manualMovementMarked?: boolean; // fmFlag -- not in the documented frame layout, always unset
  tocoResetMarked?: boolean;      // tocoFlag -- not in the documented frame layout, always unset
}

// ─────────────────────────────────────────────────────────────────────────
// READ protocol: frame reassembly + parsing
// Source: Keyar_Echo_BLE_Integration_Reference.docx, sections 1 and 2.
// ─────────────────────────────────────────────────────────────────────────

// Notify characteristic UUIDs -- both are subscribed; frames can arrive on
// either. No dedicated service UUID is used for filtering by the vendor's
// own SDK (it iterates every service the device exposes and matches
// directly on these characteristic UUIDs). Web Bluetooth's permission model
// has no such "iterate everything" mode -- GATT access must be granted to
// specific service UUIDs up front (see CANDIDATE_SERVICE_UUIDS in
// fetal-doppler-manager.service.ts for how this is worked around there).
export const NOTIFY_CHARACTERISTIC_UUID_PRIMARY = '0000fed6-0000-1000-8000-00805f9b34fb';
export const NOTIFY_CHARACTERISTIC_UUID_SECONDARY = '0000fff1-0000-1000-8000-00805f9b34fb';
export const WRITE_CHARACTERISTIC_UUID = '0000fff2-0000-1000-8000-00805f9b34fb';
export const CCCD_DESCRIPTOR_UUID = '00002902-0000-1000-8000-00805f9b34fb';

const SYNC_BYTE_1 = 0x55;
const SYNC_BYTE_2 = 0xaa;

export type FetalDopplerFrameType =
  | 'FHR_LEGACY' | 'FHR' | 'SOUND_1' | 'SOUND' | 'FHR2' | 'SOUND2' | 'MHR' | 'START' | 'STOP';

interface FrameSpec {
  type: FetalDopplerFrameType;
  length: number;
}

// Type byte -> { name, frame length }, straight from section 2.1's table.
const FRAME_SPECS: { [typeByte: number]: FrameSpec } = {
  0x01: { type: 'FHR_LEGACY', length: 10 },
  0x03: { type: 'FHR', length: 10 },
  0x08: { type: 'SOUND_1', length: 107 },
  0x09: { type: 'SOUND', length: 107 },
  0x13: { type: 'FHR2', length: 12 },
  0x19: { type: 'SOUND2', length: 107 },
  0x20: { type: 'MHR', length: 9 },
  0x30: { type: 'START', length: 8 },
  0x31: { type: 'STOP', length: 8 }
};

const CIRCULAR_BUFFER_SIZE = 4096;

// Diagnostic only -- the raw fhr1/fhr2/toco/afm byte values from an
// FHR/FHR_LEGACY/FHR2 frame regardless of whether their "has data" flag bit
// was set. Added after a real hardware test where a reference/partner app
// showed continuously live-updating values off the same device while this
// app's flag-gated `reading.fetalHeartRate` never once populated -- without
// seeing the raw byte too, there was no way to tell "the flag semantics
// here mean something narrower than we assumed, and there's a real,
// plausible number being silently withheld" apart from "the device
// genuinely has nothing to report yet." See the manager service's use of
// this for the actual diagnostic logging.
export interface RawHeartRateFields {
  fhr1: number;
  fhr2: number;
  toco: number;
  afm: number;
  powerByte: number;
}

export interface ParsedFetalDopplerFrame {
  type: FetalDopplerFrameType;
  // Present for FHR / FHR_LEGACY / FHR2 / MHR -- the fields this frame
  // reports. Absent for START / STOP (protocol acks, no reading) and for
  // SOUND / SOUND_1 / SOUND2 (audio frames -- see audioPayload instead).
  reading?: FetalDopplerReading;
  // Present for SOUND / SOUND_1 / SOUND2 only -- the 100-byte ADPCM sample
  // payload (frame bytes 3-102 per the reference doc's section 2.1), ready
  // for FetalDopplerAudioDecoder below.
  audioPayload?: Uint8Array;
  // Present for FHR / FHR_LEGACY / FHR2 only -- see RawHeartRateFields above.
  rawHeartRateFields?: RawHeartRateFields;
}

function bit(byte: number, index: number): boolean {
  return ((byte >> index) & 1) === 1;
}

// FHR / FHR_LEGACY (10 bytes): fhr1[3], fhr2[4], toco[5], afm[6];
// [7]: bits 0-1 signal quality, bit 2 afmFlag;
// [8]: bits 0-3 devicePower, bits 4-7 has-Fhr1/Fhr2/Toco/Afm flags.
// FHR2 (12 bytes) is the same field set shifted two bytes later to make
// room for the leading 2-byte monitor index, plus a trailing checksum.
function parseHeartRateFrame(raw: Uint8Array, offset: number): { reading: FetalDopplerReading; rawFields: RawHeartRateFields } {
  const fhr1 = raw[offset];
  const fhr2 = raw[offset + 1];
  const toco = raw[offset + 2];
  const afm = raw[offset + 3];
  const signalByte = raw[offset + 4];
  const powerByte = raw[offset + 5];

  // FIX (confirmed against the vendor's own LKCDecodeHeart.h): bits 4-7 of
  // powerByte are isRate/isRate2/isToco/isAfm -- per that header's own
  // comment, these mean "whether the data packet CONTAINS this parameter"
  // (a frame-content flag), NOT "is there a valid signal lock." The header
  // documents `rate`'s OWN semantics separately and explicitly: "没有时为0"
  // -- "when [no signal is] absent, it IS 0". The device already
  // self-encodes "no lock" as a real 0 value; gating on the isRate flag on
  // top of that was doubly conservative and, per a real hardware test
  // against a reference partner app that shows continuously live-updating
  // values (including while unlocked) off this same protocol, was hiding a
  // real, live-updating number this whole time. Reporting the raw byte
  // unconditionally -- letting 0 mean exactly what the vendor's own device
  // already means by it -- matches their reference app's behavior. (Callers
  // that need to exclude "no signal" 0 values from an average should filter
  // on the value itself being 0, not look for a missing field anymore.)
  const reading: FetalDopplerReading = {
    signalQuality: signalByte & 0x03,
    autoMovementDetected: bit(signalByte, 2),
    batteryLevel: powerByte & 0x0f,
    fetalHeartRate: fhr1,
    fetalHeartRate2: fhr2,
    tocoValue: toco,
    fetalMovementCurve: afm
  };
  return { reading, rawFields: { fhr1, fhr2, toco, afm, powerByte } };
}

// Parses one already-length-verified raw frame (sync + type + payload).
// Returns null for a frame that fails validation (FHR2's checksum) or that
// carries no reading (START/STOP/SOUND*) -- callers should treat null as
// "nothing to apply to state," not an error.
export function parseFetalDopplerFrame(type: FetalDopplerFrameType, raw: Uint8Array): ParsedFetalDopplerFrame | null {
  switch (type) {
    case 'FHR_LEGACY':
    case 'FHR': {
      const { reading, rawFields } = parseHeartRateFrame(raw, 3);
      return { type, reading, rawHeartRateFields: rawFields };
    }

    case 'FHR2': {
      // checksum[11] = sum(bytes 0-10) & 0xFF -- documented integrity check;
      // a mismatch means a corrupt frame, so it's dropped rather than risk
      // showing a wrong reading.
      let sum = 0;
      for (let i = 0; i <= 10; i++) sum += raw[i];
      if ((sum & 0xff) !== raw[11]) {
        return null;
      }
      const { reading, rawFields } = parseHeartRateFrame(raw, 5);
      return { type, reading, rawHeartRateFields: rawFields };
    }

    case 'MHR':
      return { type, reading: { maternalHeartRate: raw[3] } };

    case 'START':
    case 'STOP':
      return { type };

    case 'SOUND':
    case 'SOUND_1':
    case 'SOUND2':
      // "100 samples from byte 3" (section 2.1) -- bytes 3-102 inclusive.
      return { type, audioPayload: raw.slice(3, 103) };

    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// SOUND / SOUND_1 / SOUND2 audio decode.
// Confirmed against the vendor's own compiled decoder, not just the
// reference doc: LMTPDecoder.a (the SDK binary provided) contains
// LKCSoundDecoder1.5.o with its DWARF debug info intact -- disassembled via
// otool/dwarfdump (same legitimate interoperability-inspection approach
// already used for the write commands, see this file's header comment).
// That confirmed three things the doc's "100 samples from byte 3" summary
// got wrong or omitted:
//   1. It's standard 2-codes-per-byte IMA ADPCM (high nibble decoded first,
//      then low), not one code per byte -- 200 samples per 100-byte
//      payload, not 100. The step/index tables below are byte-for-byte
//      identical to the vendor's own _step_table/_index_adjust globals.
//   2. The decoded predictor is scaled by 25 before becoming the actual
//      PCM sample (`lame_encode_buffer` is fed exactly this scaled value,
//      truncated to 16 bits with wraparound, not re-clamped -- replicated
//      as-is below rather than "improved" on, to match the real device).
//   3. lame_set_in_samplerate(gfp, 0xfa0) -- FETAL_DOPPLER_AUDIO_SAMPLE_RATE_HZ
//      is 4000 Hz, confirmed, not a guess.
// Predictor/step state runs continuously across all frames in a recording
// session (reset at its start) -- confirmed as the real behavior too: the
// decoder function reads no per-frame resync state from the frame itself.
export const FETAL_DOPPLER_AUDIO_SAMPLE_RATE_HZ = 4000;

const ADPCM_STEP_TABLE = [
  7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 19, 21, 23, 25, 28, 31, 34, 37, 41, 45, 50, 55, 60, 66, 73, 80, 88, 97, 107,
  118, 130, 143, 157, 173, 190, 209, 230, 253, 279, 307, 337, 371, 408, 449, 494, 544, 598, 658, 724, 796, 876, 963,
  1060, 1166, 1282, 1411, 1552, 1707, 1878, 2066, 2272, 2499, 2749, 3024, 3327, 3660, 4026, 4428, 4871, 5358, 5894,
  6484, 7132, 7845, 8630, 9493, 10442, 11487, 12635, 13899, 15289, 16818, 18500, 20350, 22385, 24623, 27086, 29794,
  32767
];
const ADPCM_INDEX_TABLE = [-1, -1, -1, -1, 2, 4, 6, 8];

export class FetalDopplerAudioDecoder {
  private predictor = 0;
  private stepIndex = 0;

  reset(): void {
    this.predictor = 0;
    this.stepIndex = 0;
  }

  // Decodes one SOUND frame's 100-byte ADPCM payload into 200 16-bit PCM
  // samples (high nibble first, then low, per byte), carrying
  // predictor/step state forward for the next call.
  decode(payload: Uint8Array): Int16Array {
    const out = new Int16Array(payload.length * 2);
    for (let i = 0; i < payload.length; i++) {
      const byte = payload[i];
      out[i * 2] = this.decodeSample((byte >> 4) & 0x0f);
      out[i * 2 + 1] = this.decodeSample(byte & 0x0f);
    }
    return out;
  }

  private decodeSample(code: number): number {
    const step = ADPCM_STEP_TABLE[this.stepIndex];
    let diff = step >> 3;
    if (code & 4) diff += step;
    if (code & 2) diff += step >> 1;
    if (code & 1) diff += step >> 2;
    this.predictor += (code & 8) ? -diff : diff;
    this.predictor = Math.max(-32768, Math.min(32767, this.predictor));
    this.stepIndex = Math.max(0, Math.min(88, this.stepIndex + ADPCM_INDEX_TABLE[code & 7]));
    // Confirmed gain stage (see this class's header comment) -- the
    // vendor's own decoder scales the clamped predictor by 25 before
    // treating it as the PCM sample, truncating to 16 bits with
    // wraparound (not re-clamping) on overflow. (x << 16) >> 16 performs
    // that same truncation in JS.
    return ((this.predictor * 25) << 16) >> 16;
  }
}

// Wraps raw mono 16-bit PCM in a minimal 44-byte WAV/RIFF header -- no
// encoding library needed, and every browser plays WAV natively via <audio>.
export function pcmToWavBlob(samples: Int16Array, sampleRateHz: number): Blob {
  const bytesPerSample = 2;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRateHz, true);
  view.setUint32(28, sampleRateHz * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    view.setInt16(offset, samples[i], true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

// Section 2.2's reassembly algorithm: a 4096-byte circular buffer that
// overwrites the oldest data once full, resyncing on the 0x55 0xAA marker
// one byte at a time, and discarding just the 3 header bytes (not the whole
// buffer) on an unrecognized type so the stream self-recovers from
// corruption or dropped data without stalling.
//
// FIX: a complete frame is now extracted as soon as ITS OWN declared length
// (from FRAME_SPECS, once the type byte is readable) is fully buffered, not
// only once a worst-case-sized (107-byte) backlog has piled up. The
// original unconditional `count > MAX_FRAME_LENGTH` threshold meant that
// whenever the buffer drained back down to <=107 bytes -- normal during
// real-time BLE streaming, where notifications tend to arrive paced close
// to real time rather than in big bursts -- a fully-present short frame
// (FHR/MHR/START/STOP, 8-12 bytes) or even an exactly-107-byte SOUND frame
// sat queued, unextracted, until enough MORE bytes arrived to push the
// backlog back over threshold. Audio frames delayed/starved this way are
// still eventually decoded during a long-running session (more data keeps
// arriving), but whatever is still short of the threshold at the moment
// stopAudioCapture() snapshots audioChunks is permanently lost -- for a
// short recording this can be most of it, producing a near-empty (or
// 0:00-duration) WAV that "can't play" despite the device having genuinely
// streamed several seconds of audio. Extracting on each frame's own length
// removes that arbitrary residue entirely (at most one frame's worth of
// bytes is ever left mid-flight, same as any standard length-prefixed
// framing).
export class FetalDopplerFrameReassembler {
  private readonly buffer = new Uint8Array(CIRCULAR_BUFFER_SIZE);
  private inPtr = 0;
  private outPtr = 0;
  private count = 0;

  // Feeds newly-arrived notification bytes in and returns every complete
  // frame that could be extracted as a result (zero or more -- a single
  // BLE notification rarely lines up with exactly one logical frame).
  push(bytes: Uint8Array | DataView): ParsedFetalDopplerFrame[] {
    const incoming = bytes instanceof DataView
      ? new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength)
      : bytes;

    for (let i = 0; i < incoming.length; i++) {
      this.buffer[this.inPtr] = incoming[i];
      this.inPtr = (this.inPtr + 1) % CIRCULAR_BUFFER_SIZE;
      if (this.count < CIRCULAR_BUFFER_SIZE) {
        this.count++;
      } else {
        // Full -- the write above already overwrote the oldest byte, so the
        // read pointer must follow to stay past it.
        this.outPtr = (this.outPtr + 1) % CIRCULAR_BUFFER_SIZE;
      }
    }

    const frames: ParsedFetalDopplerFrame[] = [];
    // Need at least sync(2) + type(1) buffered to even know which frame is
    // next / how long it is.
    while (this.count >= 3) {
      if (this.byteAt(0) !== SYNC_BYTE_1 || this.byteAt(1) !== SYNC_BYTE_2) {
        this.consume(1);
        continue;
      }

      const spec = FRAME_SPECS[this.byteAt(2)];
      if (!spec) {
        // Valid sync, unrecognized type -- noise. Only the header is
        // discarded so a real frame that happens to start right after it
        // isn't skipped over too.
        this.consume(3);
        continue;
      }

      // This specific frame isn't fully buffered yet -- stop and wait for
      // more bytes (the next push() call), rather than extracting past what
      // has actually arrived.
      if (this.count < spec.length) break;

      const raw = this.extract(spec.length);
      this.consume(spec.length);
      const parsed = parseFetalDopplerFrame(spec.type, raw);
      if (parsed) frames.push(parsed);
    }
    return frames;
  }

  private byteAt(offsetFromOut: number): number {
    return this.buffer[(this.outPtr + offsetFromOut) % CIRCULAR_BUFFER_SIZE];
  }

  private extract(length: number): Uint8Array {
    const out = new Uint8Array(length);
    for (let i = 0; i < length; i++) out[i] = this.byteAt(i);
    return out;
  }

  private consume(n: number): void {
    this.outPtr = (this.outPtr + n) % CIRCULAR_BUFFER_SIZE;
    this.count -= n;
  }

  // Drops any partially-buffered, in-flight frame bytes -- call on
  // disconnect/reconnect so a stale partial frame from the previous
  // connection can never get spliced together with bytes from a new one.
  reset(): void {
    this.inPtr = 0;
    this.outPtr = 0;
    this.count = 0;
  }
}
